import React, { useState, useMemo, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Modal,
  StyleSheet,
  ActivityIndicator,
} from "react-native";

type FolderNode = {
  id: number;
  name: string;
  type: string;
  parentId: any;
  deleted: number;
  children: FolderNode[];
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onMove: (destinationFolderId: number) => Promise<void>;
  records: any[];
  selectedItemIds: number[];
  currentFolderId: number | null;
};

export default function ProjectRecordMoveModal({
  visible,
  onClose,
  onMove,
  records,
  selectedItemIds,
  currentFolderId,
}: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [destinationId, setDestinationId] = useState<number | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  const folderRoot = useMemo(() => buildFolderTree(records), [records]);

  const disabledIds = useMemo(
    () => computeDisabledIds(folderRoot, selectedItemIds),
    [folderRoot, selectedItemIds]
  );

  // Reset state and auto-expand root each time modal opens
  useEffect(() => {
    if (!visible) return;
    setDestinationId(null);
    if (folderRoot?.id != null) {
      setExpandedIds(new Set([folderRoot.id]));
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  function buildFolderTree(flat: any[]): FolderNode | null {
    const map = new Map<number, FolderNode>();
    let root: FolderNode | null = null;

    flat.forEach((item) => {
      if (item.type === "file") return;
      map.set(item.id, { ...item, children: [] });
      if (item.type === "library") root = map.get(item.id) ?? null;
    });

    flat.forEach((item) => {
      if (item.type === "file" || item.type === "library") return;
      const parent = map.get(item.parentId);
      const node = map.get(item.id);
      if (parent && node) parent.children.push(node);
    });

    return root;
  }

  // Returns the set of folder IDs that should not be selectable as a destination:
  // the selected items themselves plus all of their descendants.
  function computeDisabledIds(
    root: FolderNode | null,
    selectedIds: number[]
  ): Set<number> {
    const disabled = new Set<number>();
    if (!root) return disabled;

    function collectAll(node: FolderNode) {
      disabled.add(node.id);
      node.children.forEach(collectAll);
    }

    function traverse(node: FolderNode) {
      if (selectedIds.includes(node.id)) {
        collectAll(node);
      } else {
        node.children.forEach(traverse);
      }
    }

    traverse(root);
    return disabled;
  }

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function renderNode(node: FolderNode, depth: number): React.ReactNode {
    if (node.deleted === 1) return null;

    const isDisabled = disabledIds.has(node.id);
    const isCurrent = node.id === currentFolderId;
    const isSelected = node.id === destinationId;
    const subFolders = node.children.filter((c) => c.deleted !== 1);
    const hasChildren = subFolders.length > 0;
    const isExpanded = expandedIds.has(node.id);

    return (
      <View key={node.id}>
        <TouchableOpacity
          style={[
            styles.nodeRow,
            { paddingLeft: Math.min(depth, 8) * 16 + 8 },
            isSelected && styles.selectedRow,
            (isDisabled || isCurrent) && styles.disabledRow,
          ]}
          disabled={isDisabled || isCurrent}
          onPress={() => setDestinationId(node.id)}
          activeOpacity={0.7}
        >
          <TouchableOpacity
            style={styles.chevronBtn}
            onPress={(e) => {
              e.stopPropagation();
              if (hasChildren) toggleExpand(node.id);
            }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={styles.chevronText}>
              {hasChildren ? (isExpanded ? "▾" : "▸") : ""}
            </Text>
          </TouchableOpacity>
          <Text
            style={[
              styles.nodeName,
              (isDisabled || isCurrent) && styles.disabledText,
              isSelected && styles.selectedText,
            ]}
            numberOfLines={1}
          >
            📁 {node.name}
          </Text>
          {isCurrent && <Text style={styles.currentBadge}>current</Text>}
        </TouchableOpacity>
        {isExpanded && subFolders.map((child) => renderNode(child, depth + 1))}
      </View>
    );
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => { if (!isMoving) onClose(); }}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <Text style={styles.title}>Move To</Text>
          <Text style={styles.subtitle}>Select a destination folder</Text>

          <ScrollView style={styles.treeScroll} nestedScrollEnabled>
            {folderRoot ? renderNode(folderRoot, 0) : null}
          </ScrollView>

          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={[styles.moveButton, (!destinationId || isMoving) && styles.disabledButton]}
              disabled={!destinationId || isMoving}
              onPress={async () => {
                setIsMoving(true);
                try {
                  await onMove(destinationId!);
                  onClose();
                } finally {
                  setIsMoving(false);
                }
              }}
            >
              {isMoving
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={styles.moveButtonText}>Move Here</Text>
              }
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.cancelButton, isMoving && styles.disabledButton]}
              disabled={isMoving}
              onPress={onClose}
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    width: "60%",
    maxWidth: 520,
    backgroundColor: "white",
    borderRadius: 10,
    padding: 24,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    color: "#666",
    marginBottom: 12,
  },
  treeScroll: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 6,
    maxHeight: 320,
    marginBottom: 16,
  },
  nodeRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingRight: 12,
  },
  selectedRow: {
    backgroundColor: "#dbeeff",
    borderRadius: 4,
  },
  disabledRow: {
    opacity: 0.4,
  },
  chevronBtn: {
    width: 20,
    alignItems: "center",
    marginRight: 4,
  },
  chevronText: {
    fontSize: 12,
    color: "#555",
  },
  nodeName: {
    fontSize: 14,
    color: "#333",
    flex: 1,
  },
  selectedText: {
    color: "#0078D4",
    fontWeight: "600",
  },
  disabledText: {
    color: "#aaa",
  },
  currentBadge: {
    fontSize: 11,
    color: "#fff",
    backgroundColor: "#888",
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  moveButton: {
    backgroundColor: "#0078D4",
    borderRadius: 5,
    paddingVertical: 10,
    paddingHorizontal: 20,
    alignItems: "center",
  },
  disabledButton: {
    backgroundColor: "#ccc",
  },
  moveButtonText: {
    color: "white",
    fontWeight: "600",
    fontSize: 14,
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    paddingVertical: 10,
    paddingHorizontal: 20,
    alignItems: "center",
  },
  cancelText: {
    color: "#555",
    fontSize: 14,
  },
});
